import { useEffect, useState } from 'react';
import { Form, FormItem } from 'components/Base/Form';
import { Form as AntdForm } from 'antd';
import { CustomSelect, Select, SelectWithAdd } from 'components/Base/Select';
import { useGetOrganizationsIdentityQuery } from 'redux/apiSlice/organizations.slice';
import { useGetElcIdentitiesQuery } from 'redux/apiSlice/elcs.slice';
import CreateOrganizationModal from 'components/Shared/CreateOrganizationModal';
import { RadioGroup } from 'components/Base/RadioGroup';
import { TOrganizationsIdentityUI } from 'data/types/organizations.types';
import { TCreateOrganizationModalOpen } from 'components/Shared/CreateOrganizationModal/CreateOrganizationModal.types';
import useGetData from './CreateWorkshopForm.data';
import { TCreateWorkshopFormProps } from './CreateWorkshopForm.types';

enum EHostType {
  ORGANIZATION = 'ORGANIZATION',
  ELC = 'ELC',
}

const CreateWorkshopForm = ({ form }: TCreateWorkshopFormProps) => {
  const [open, setOpen] = useState<TCreateOrganizationModalOpen>({
    visible: false,
    initialOrgName: '',
  });
  const [selectedOrganization, setSelectedOrganization] =
    useState<TOrganizationsIdentityUI>();
  const hostType =
    AntdForm.useWatch('hostType', form) || EHostType.ORGANIZATION;
  const { createWorkshopFormFields, typeData, hostTypeOptions } = useGetData();
  const { host, type, attendanceType, hostedPrivately } =
    createWorkshopFormFields;
  const { isLoading: isLoadingOrgs, data: organizations } =
    useGetOrganizationsIdentityQuery();
  const { isLoading: isLoadingElcs, data: elcs } = useGetElcIdentitiesQuery();

  useEffect(() => {
    form.setFieldValue('host', selectedOrganization?.value);
  }, [selectedOrganization]);

  // Clear host field when host type changes
  useEffect(() => {
    form.setFieldValue('host', undefined);
    setSelectedOrganization(undefined);
  }, [hostType, form]);

  // Determine which data to use based on hostType
  const hostOptions =
    hostType === EHostType.ORGANIZATION ? organizations : elcs;
  const isLoadingHosts =
    hostType === EHostType.ORGANIZATION ? isLoadingOrgs : isLoadingElcs;

  return (
    <>
      <Form form={form}>
        <FormItem {...type}>
          <Select
            placement="topLeft"
            placeholder={type.placeholder}
            options={typeData}
          />
        </FormItem>
        <FormItem {...attendanceType}>
          <RadioGroup
            optionType="button"
            buttonStyle="solid"
            options={attendanceType.options}
            defaultValue={attendanceType.initialValue}
          />
        </FormItem>
        <FormItem {...hostedPrivately}>
          <RadioGroup
            optionType="button"
            buttonStyle="solid"
            options={hostedPrivately.options}
            defaultValue={hostedPrivately.initialValue}
          />
        </FormItem>
        <FormItem
          label="Host Type"
          name="hostType"
          initialValue={EHostType.ORGANIZATION}
        >
          <RadioGroup
            optionType="button"
            buttonStyle="solid"
            options={hostTypeOptions}
          />
        </FormItem>
        <FormItem {...host}>
          {hostType === EHostType.ORGANIZATION ? (
            <SelectWithAdd
              placeholder={host.placeholder}
              loading={isLoadingHosts}
              options={hostOptions}
              onBtnClick={currentSearchValue => {
                setOpen({
                  visible: true,
                  initialOrgName: currentSearchValue || '',
                });
              }}
              addButtonLabel="Add"
            />
          ) : (
            <CustomSelect
              placeholder={host.placeholder}
              loading={isLoadingHosts}
              options={hostOptions}
              showSearch
              filterOption={(input, option) =>
                String(option?.label ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          )}
        </FormItem>
      </Form>
      <CreateOrganizationModal
        open={open}
        setOpen={setOpen}
        setSelectedOrganization={setSelectedOrganization}
      />
    </>
  );
};

export default CreateWorkshopForm;
